import React, { useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Button, Icon } from '@node-space/ui-components'
import { useWalletContext } from 'hooks/context'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import WalletModal, { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ActionIds } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { PayActionBeneficiaryTypes } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface WalletButtonsFiatProps {
  disabled?: boolean
}

export const WalletButtonsFiat = ({ disabled }: WalletButtonsFiatProps) => {
  const { t } = useTranslation()
  const { enableUsLaunchRestrictions, enabledUnifiedFiatSendFlow } = useFeatureFlags()
  const permissions = useMappedRoles()
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(undefined)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const queryClient = useQueryClient()
  const wallet = useWalletContext()
  const [, setWalletAction] = useContext(WalletActionContext)
  const customerModel = useAtomValue(customerModelAtom)

  // Unified Payouts
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setFiatBeneficiaryAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: reactQueryKeys.wallet(wallet.id as number) })
  }

  const isSelectedWalletActive = useIsActiveWallet(wallet)

  const handleOpenPayModal = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_PAY, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
    })
    setWalletModalAction({
      key: 'pay',
      data: {
        id: String(wallet.id),
      },
      metadata: {
        pay: { beneficiaryPaymentType: PayActionBeneficiaryTypes.FIAT },
      },
    })
  }

  const handleOpenWithdrawModal = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_WITHDRAW, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
    })
    setWalletModalAction({
      key: 'withdraw',
      data: {
        id: String(wallet.id),
      },
    })
  }

  const handleOpenMoveSheet = () => {
    setWalletAction({
      actionId: ActionIds.MOVE,
      header: t('customers.moveFunds'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWalletType: WalletTransferType.MAIN,
        sourceWallet: wallet,
        isEmbeddedCustomerModel: customerModel === CustomerModels.EMBEDDED,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenConvertSheet = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_CONVERT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
      isFiat: true,
    })
    setWalletAction({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const canMakeNetworkTranfer = permissions?.bvnkNetwork?.canTrade
  const isPayEnabled = wallet?.supportsInternalBvnkNetworkTransfers
    ? canMakeNetworkTranfer
    : !disabled

  const handleOpenFiatDepositSheet = () => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        sourceWalletId: wallet?.id,
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenSendFiatSheet = () => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: wallet?.lsid,
      })

      setIsSheetOpen(true)
    }
  }

  return (
    isSelectedWalletActive && (
      <Box flex padding={8} className="gap-4 sm:pt-0">
        {!enabledUnifiedFiatSendFlow && wallet?.supportsThirdParty && (
          <Button
            testId="buttonPay"
            disabled={!isPayEnabled}
            onClick={handleOpenPayModal}
            iconElement={<Icon name="CreditCard" />}
          >
            {t('pay')}
          </Button>
        )}
        {!enableUsLaunchRestrictions && (
          <Button
            testId="buttonConvert"
            disabled={disabled}
            onClick={handleOpenConvertSheet}
            iconElement={<Icon name="ArrowsLeftRight" />}
          >
            {t('convert')}
          </Button>
        )}
        {!enabledUnifiedFiatSendFlow && wallet?.supportsWithdrawals && (
          <Button
            testId="buttonWithdraw"
            disabled={disabled}
            onClick={handleOpenWithdrawModal}
            iconElement={<Icon name="CoinArrowOutCustom" />}
          >
            {t('withdraw')}
          </Button>
        )}
        {enabledUnifiedFiatSendFlow &&
          (wallet?.supportsWithdrawals || wallet?.supportsThirdParty) && (
            <Button
              testId="sendFiat"
              disabled={disabled}
              onClick={handleOpenSendFiatSheet}
              iconElement={<Icon name="CoinArrowOutCustom" />}
            >
              {t('wallets.fiatSend.sendFiat')}
            </Button>
          )}
        {wallet?.supportsDeposits && (
          <Button
            testId="buttonDeposit"
            disabled={disabled}
            onClick={handleOpenFiatDepositSheet}
            iconElement={<Icon name="CoinArrowInCustom" />}
          >
            {t('deposit')}
          </Button>
        )}
        {wallet?.supportsInternalTransfers && (
          <Button
            testId="buttonMove"
            disabled={disabled}
            onClick={handleOpenMoveSheet}
            iconElement={<Icon name="ArrowsLeftRight" />}
          >
            {t('customers.move')}
          </Button>
        )}
        <WalletModal
          action={walletModalAction}
          setWalletModalAction={setWalletModalAction}
          refresh={handleRefresh}
        />
        <WalletSheet isSheetOpen={isSheetOpen} />
      </Box>
    )
  )
}
